import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
} from '@mui/material';
import { MarkEmailRead, Visibility, Reply, Email, Phone, Business, ChatBubbleOutline, ContentCopy, Check, Person, Delete, DeleteSweep } from '@mui/icons-material';
import { getContacts, markContactRead, deleteContact, deleteContactsBulk } from '../../services/api';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const handleCopy = async (value, fieldLabel) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldLabel);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedIds([]);
    try {
      const readParam = filter === 'all' ? undefined : filter === 'read';
      const res = await getContacts({ page: page + 1, limit: rowsPerPage, read: readParam });
      setContacts(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleMarkRead = async (id) => {
    try {
      await markContactRead(id);
      fetchContacts();
    } catch (err) {
      setError(err.message || 'Failed to mark as read');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteContact(id);
      setDeleteConfirm(null);
      setSelected(null);
      fetchContacts();
    } catch (err) {
      setError(err.message || 'Failed to delete contact');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await deleteContactsBulk(selectedIds);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      fetchContacts();
    } catch (err) {
      setError(err.message || 'Failed to delete contacts');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(contacts.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: FONT_HEADING,
            fontWeight: 700,
            background: `linear-gradient(135deg, ${SECONDARY} 0%, ${PRIMARY} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Contact Submissions
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={filter}
          exclusive
          onChange={(_, v) => { if (v) { setFilter(v); setPage(0); } }}
          sx={{
            gap: 1,
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              '&:not(:first-of-type)': { borderRadius: RADIUS, ml: 0 },
              '&:first-of-type': { borderRadius: RADIUS },
            },
            '& .MuiToggleButton-root': {
              fontFamily: FONT_BODY,
              textTransform: 'none',
              borderRadius: RADIUS,
              px: 2.5,
              py: 0.75,
              fontSize: '0.875rem',
              fontWeight: 500,
              border: `2px solid rgba(108, 152, 173, 0.2)`,
              color: SECONDARY,
              transition: TRANSITION,
              '&:hover': {
                backgroundColor: 'rgba(108, 152, 173, 0.08)',
                borderColor: PRIMARY,
              },
              '&.Mui-selected': {
                backgroundColor: SECONDARY,
                color: '#fff',
                borderColor: SECONDARY,
                boxShadow: '0 2px 8px rgba(36, 39, 42, 0.25)',
                '&:hover': {
                  backgroundColor: '#3d4348',
                  borderColor: '#3d4348',
                },
              },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="unread">Unread</ToggleButton>
          <ToggleButton value="read">Read</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Bulk Delete Bar */}
      {selectedIds.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: RADIUS,
            background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.08) 0%, rgba(211, 47, 47, 0.04) 100%)',
            border: '2px solid rgba(211, 47, 47, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY }}>
            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
          </Typography>
          <Button
            variant="contained"
            startIcon={<DeleteSweep />}
            onClick={() => setBulkDeleteConfirm(true)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Delete Selected
          </Button>
          <Button
            onClick={() => setSelectedIds([])}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 500,
              color: '#666',
            }}
          >
            Clear Selection
          </Button>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : contacts.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: RADIUS }}>No contact submissions found.</Alert>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: RADIUS,
            border: '2px solid rgba(108, 152, 173, 0.12)',
            boxShadow: CARD_SHADOW,
            overflow: 'hidden',
            transition: TRANSITION,
            '&:hover': { boxShadow: CARD_SHADOW_HOVER },
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: 'rgba(108, 152, 173, 0.05)' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.length > 0 && selectedIds.length < contacts.length}
                      checked={contacts.length > 0 && selectedIds.length === contacts.length}
                      onChange={handleSelectAll}
                      sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY }, '&.MuiCheckbox-indeterminate': { color: PRIMARY } }}
                    />
                  </TableCell>
                  {['Status', 'Name', 'Company', 'Email', 'Phone', 'Date', ''].map((h, i) => (
                    <TableCell
                      key={h || 'actions'}
                      align={i === 6 ? 'right' : 'left'}
                      sx={{ fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '0.8rem', color: SECONDARY, letterSpacing: 0.3 }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow
                    key={c.id}
                    hover
                    selected={selectedIds.includes(c.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: selectedIds.includes(c.id) ? 'rgba(108, 152, 173, 0.12)' : c.read ? 'transparent' : 'rgba(108, 152, 173, 0.04)',
                      transition: TRANSITION,
                      '&:hover': { bgcolor: 'rgba(108, 152, 173, 0.07)' },
                    }}
                    onClick={() => setSelected(c)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(c.id)}
                        onChange={() => handleSelectOne(c.id)}
                        sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.read ? 'Read' : 'New'}
                        size="small"
                        sx={{
                          fontFamily: FONT_BODY,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          borderRadius: '8px',
                          ...(c.read
                            ? { bgcolor: 'rgba(0,0,0,0.06)', color: '#888' }
                            : { background: `linear-gradient(135deg, ${SECONDARY} 0%, #3d4348 100%)`, color: '#fff' }),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: c.read ? 400 : 600, fontFamily: FONT_BODY, color: SECONDARY }}>
                      {c.name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT_BODY, color: '#555' }}>{c.company || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_BODY, color: '#555' }}>{c.email}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_BODY, color: '#555' }}>{c.phone || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_BODY, color: '#888', fontSize: '0.82rem' }}>
                      {formatDate(c.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setSelected(c); }}
                          sx={{ color: PRIMARY, transition: TRANSITION, '&:hover': { transform: 'scale(1.15)' } }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!c.read && (
                        <Tooltip title="Mark as read">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleMarkRead(c.id); }}
                            sx={{ color: PRIMARY, transition: TRANSITION, '&:hover': { transform: 'scale(1.15)' } }}
                          >
                            <MarkEmailRead fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(c); }}
                          sx={{ color: '#d32f2f', transition: TRANSITION, '&:hover': { transform: 'scale(1.15)' } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            sx={{ fontFamily: FONT_BODY }}
          />
        </Paper>
      )}

      {/* Detail dialog */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '1.25rem',
            boxShadow: '0 24px 48px rgba(36, 39, 42, 0.18)',
            border: 'none',
            overflow: 'hidden',
          },
        }}
      >
        {selected && (
          <>
            {/* Header with gradient background */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${SECONDARY} 0%, #3d4348 100%)`,
                px: 4,
                py: 3.5,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontFamily: FONT_HEADING,
                  fontWeight: 700,
                  color: '#fff',
                  mb: 0.5,
                }}
              >
                {selected.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: FONT_BODY,
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.9rem',
                }}
              >
                Contact Submission • {formatDate(selected.createdAt)}
              </Typography>
            </Box>

            <DialogContent sx={{ p: 0 }}>
              {/* Contact Info Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: 0,
                  borderBottom: '1px solid rgba(108, 152, 173, 0.12)',
                }}
              >
                {[
                  { icon: <Person sx={{ fontSize: 20 }} />, label: 'Name', value: selected.name },
                  selected.company && { icon: <Business sx={{ fontSize: 20 }} />, label: 'Company', value: selected.company },
                  { icon: <Email sx={{ fontSize: 20 }} />, label: 'Email', value: selected.email, copyable: true },
                  selected.phone && { icon: <Phone sx={{ fontSize: 20 }} />, label: 'Phone', value: selected.phone, copyable: true },
                ].filter(Boolean).map((f, idx, arr) => {
                  const isCopied = copiedField === f.label;
                  const content = (
                    <Box
                      key={f.label}
                      onClick={f.copyable ? () => handleCopy(f.value, f.label) : undefined}
                      sx={{
                        p: 2.5,
                        borderRight: { xs: 'none', sm: idx < arr.length - 1 ? '1px solid rgba(108, 152, 173, 0.12)' : 'none' },
                        borderBottom: { xs: idx < arr.length - 1 ? '1px solid rgba(108, 152, 173, 0.08)' : 'none', sm: 'none' },
                        transition: TRANSITION,
                        cursor: f.copyable ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: f.copyable ? 'rgba(108, 152, 173, 0.08)' : 'rgba(108, 152, 173, 0.03)',
                        },
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Box sx={{ color: isCopied ? '#4caf50' : PRIMARY, transition: TRANSITION }}>{f.icon}</Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#999',
                            fontFamily: FONT_HEADING,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            letterSpacing: 0.8,
                          }}
                        >
                          {f.label}
                        </Typography>
                        {f.copyable && (
                          <Box
                            sx={{
                              ml: 'auto',
                              color: isCopied ? '#4caf50' : '#bbb',
                              transition: TRANSITION,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {isCopied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: FONT_BODY,
                          color: isCopied ? '#4caf50' : SECONDARY,
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          wordBreak: 'break-word',
                          transition: TRANSITION,
                        }}
                      >
                        {isCopied ? 'Copied!' : f.value}
                      </Typography>
                    </Box>
                  );

                  return f.copyable ? (
                    <Tooltip
                      key={f.label}
                      title={isCopied ? 'Copied!' : `Click to copy ${f.label.toLowerCase()}`}
                      arrow
                      placement="top"
                    >
                      {content}
                    </Tooltip>
                  ) : (
                    content
                  );
                })}
              </Box>

              {/* Message Section */}
              {selected.message && (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_HOVER} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ChatBubbleOutline sx={{ color: '#fff', fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: FONT_HEADING,
                        fontWeight: 600,
                        color: SECONDARY,
                      }}
                    >
                      Message
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(108, 152, 173, 0.06) 0%, rgba(108, 152, 173, 0.02) 100%)',
                      border: '1px solid rgba(108, 152, 173, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: FONT_BODY,
                        color: SECONDARY,
                        lineHeight: 1.75,
                        fontSize: '1rem',
                      }}
                    >
                      {selected.message}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </DialogContent>

            {/* Footer Actions */}
            <DialogActions
              sx={{
                px: 3,
                py: 2.5,
                background: 'linear-gradient(180deg, rgba(108, 152, 173, 0.04) 0%, rgba(108, 152, 173, 0.08) 100%)',
                borderTop: '1px solid rgba(108, 152, 173, 0.12)',
                gap: 1.5,
              }}
            >
              <Button
                component="a"
                href={`mailto:${encodeURIComponent(selected.email)}?subject=${encodeURIComponent(`Re: Your enquiry – Tailored Alloys`)}&body=${encodeURIComponent(`Hi ${selected.name},\n\n`)}`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<Reply />}
                variant="outlined"
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: FONT_BODY,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 2.5,
                  py: 1,
                  borderColor: PRIMARY,
                  borderWidth: 2,
                  color: PRIMARY,
                  transition: TRANSITION,
                  '&:hover': {
                    borderColor: PRIMARY,
                    borderWidth: 2,
                    backgroundColor: PRIMARY,
                    color: '#fff',
                  },
                }}
              >
                Reply via Email
              </Button>
              {!selected.read && (
                <Button
                  onClick={() => { handleMarkRead(selected.id); setSelected(null); }}
                  variant="contained"
                  startIcon={<MarkEmailRead />}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    px: 2.5,
                    py: 1,
                    background: `linear-gradient(135deg, ${SECONDARY} 0%, #3d4348 100%)`,
                    boxShadow: '0 4px 14px rgba(36, 39, 42, 0.25)',
                    transition: TRANSITION,
                    '&:hover': {
                      background: `linear-gradient(135deg, #3d4348 0%, ${SECONDARY} 100%)`,
                      boxShadow: '0 6px 20px rgba(36, 39, 42, 0.35)',
                    },
                  }}
                >
                  Mark as Read
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={() => setSelected(null)}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: FONT_BODY,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  px: 2.5,
                  py: 1,
                  color: '#666',
                  transition: TRANSITION,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    color: SECONDARY,
                  },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            boxShadow: '0 24px 48px rgba(36, 39, 42, 0.18)',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
          Delete Contact Submission
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY }}>
            Are you sure you want to delete this contact submission from <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirm(null)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 500,
              color: '#666',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirm?.id)}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteConfirm}
        onClose={() => setBulkDeleteConfirm(false)}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            boxShadow: '0 24px 48px rgba(36, 39, 42, 0.18)',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
          Delete Multiple Contacts
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY }}>
            Are you sure you want to delete <strong>{selectedIds.length}</strong> contact submission{selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setBulkDeleteConfirm(false)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 500,
              color: '#666',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
